/**
 * @fileoverview System-web invitation API wrapper (online-only provisioning).
 * @module system-web/services/invitationService
 *
 * Responsibilities:
 * - Expose the system invitation endpoints to local-first services.
 * - Report invite-readiness status for the system admin UI.
 * - Provide send, resend, revoke, command, and link-generation API wrappers.
 *
 * Dependencies:
 * - system-web apiClient (apiFetch)
 *
 * Architecture:
 * - Sending an invitation email is an inherently-online provisioning action,
 *   the recognized exception to local-first reads. The user record itself is
 *   still created locally first (see systemUserService.inviteSystemUser); this
 *   module only delivers the invitation against the synced server record.
 *
 * Notes:
 * - Never called directly from components — only from systemUserService.
 */

import { apiFetch } from '../apiClient';

export interface InvitationStatus {
  inviteReady?: boolean;
  graphConfigured?: boolean;
  inviteMessage?: string;
  message?: string;
}

/**
 * @contract InviteUserInput
 * Purpose: Request body for POST /api/invitations.
 * Fields:  userId, email, firstName, lastName, role (id/name), invitedBy,
 *          department/organization scoping, inviteMessage, sendInvite.
 * Rules:   A system-admin invite omits department/organization scoping; the
 *          server then creates/assigns the platform role for `userId`.
 * Sync:    Online-only; the user document is created in Dexie before this call.
 */
export interface InviteUserInput {
  userId?: string;
  email: string;
  firstName: string;
  lastName: string;
  invitedBy?: string;
  departmentId?: string;
  departmentName?: string;
  roleId?: string;
  roleName?: string;
  organizationId?: string;
  organizationName?: string;
  orgRoleId?: string;
  orgRoleName?: string;
  inviteMessage?: string;
  sendInvite?: boolean;
}

/**
 * @contract InvitationCommandPayload
 * Purpose: Request body for POST /api/invitations/:userId/command.
 * Fields:  action, inviteMessage.
 * Rules:   System-admin invites omit department/organization scoping; the
 *          server resolves the top-level invite automatically.
 * Sync:    Online-only.
 */
export interface InvitationCommandPayload {
  action: 'send' | 'sendNew' | 'resend' | 'revoke';
  inviteMessage?: string;
}

export async function getInvitationStatus(): Promise<InvitationStatus> {
  return apiFetch('/api/invitations/status') as Promise<InvitationStatus>;
}

/**
 * @process inviteUser (system-web client wrapper)
 * Steps:            POST the invite payload to /api/invitations.
 * Inputs:           InviteUserInput
 * Outputs:          The server's authoritative user document.
 * Failure Handling: Rejects with the server error message (e.g. a 409
 *                   "has not finished syncing" sync race); callers decide
 *                   whether to retry.
 * Idempotency:      REQUIRED — the server resolves the user by id/email and
 *                   updates the existing record on repeat calls.
 */
export async function inviteUser(input: InviteUserInput): Promise<any> {
  return apiFetch('/api/invitations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * @process resendSystemInvitation (system-web client wrapper)
 * Steps:            POST to /api/invitations/:userId/resend with no membership
 *                   scope so the server resends the system-admin (top-level)
 *                   invite.
 * Inputs:           userId — the synced system user's id.
 * Outputs:          The server's authoritative, updated user document.
 * Failure Handling: Rejects with the server error message (e.g. a 409 when the
 *                   invite is no longer resendable); callers surface it.
 * Idempotency:      NOT idempotent — each call mints a fresh invite + email.
 */
export async function resendSystemInvitation(userId: string): Promise<any> {
  return apiFetch(`/api/invitations/${userId}/resend`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * @process sendInvitationCommand (system-web client wrapper)
 * Steps:            POST to /api/invitations/:userId/command with the given
 *                   action (send, sendNew, resend, revoke).
 * Inputs:           userId, InvitationCommandPayload.
 * Outputs:          The server's authoritative, updated user document.
 * Failure Handling: Rejects with the server error message; callers surface it.
 * Idempotency:      Depends on action — send is idempotent; resend is not.
 */
export async function sendInvitationCommand(
  userId: string,
  payload: InvitationCommandPayload,
): Promise<any> {
  return apiFetch(`/api/invitations/${userId}/command`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * @process revokeSystemInvitation (system-web client wrapper)
 * Steps:            POST to /api/invitations/:userId/revoke with no membership
 *                   scope so the server revokes the system-admin (top-level)
 *                   invite.
 * Inputs:           userId — the synced system user's id.
 * Outputs:          The server's authoritative, updated user document.
 * Failure Handling: Rejects with the server error message; callers surface it.
 * Idempotency:      REQUIRED — revoking an already-revoked invite is a no-op.
 */
export async function revokeSystemInvitation(userId: string): Promise<any> {
  return apiFetch(`/api/invitations/${userId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * @process generateSystemInviteLink (system-web client wrapper)
 * Steps:            POST to /api/invitations/:userId/link with no membership
 *                   scope to generate a system-admin invite URL.
 * Inputs:           userId, optional inviteMessage.
 * Outputs:          { inviteUrl, expiresAt, user }.
 * Failure Handling: Rejects with the server error message; callers surface it.
 * Idempotency:      NOT idempotent — each call mints a new link.
 */
export async function generateSystemInviteLink(
  userId: string,
  inviteMessage?: string,
): Promise<{ inviteUrl: string; expiresAt?: string; user: any }> {
  return apiFetch(`/api/invitations/${userId}/link`, {
    method: 'POST',
    body: JSON.stringify({ inviteMessage }),
  });
}
